export const LOCALE_STORAGE_KEY = "site.preferredLocale";

export function normalizeLocale(value) {
    return `${value || ""}`.trim().toLowerCase();
}

export function getKnownLocales(siteLanguages = []) {
    return siteLanguages
        .map((lang) => normalizeLocale(lang?.locale))
        .filter(Boolean);
}

export function extractLocalePrefixFromHref(hrefValue = "") {
    const href = `${hrefValue || ""}`.trim();

    if (!href) {
        return "";
    }

    try {
        const baseOrigin =
            typeof window !== "undefined"
                ? window.location.origin
                : "http://localhost";
        const parsedUrl = new URL(href, baseOrigin);

        return normalizeLocale(
            parsedUrl.pathname.split("/").filter(Boolean)[0],
        );
    } catch {
        return "";
    }
}

export function getKnownLocalePrefixes(siteLanguages = []) {
    return siteLanguages
        .map((lang) => extractLocalePrefixFromHref(lang?.href))
        .filter(Boolean);
}

export function getLocaleFromUrl(url = "", knownLocales = []) {
    const [pathPart] = `${url || ""}`.split("?");
    const firstPathSegment = normalizeLocale(
        pathPart.split("/").filter(Boolean)[0],
    );

    if (!firstPathSegment) {
        return "";
    }

    if (!knownLocales.length) {
        return /^[a-z]{2}$/i.test(firstPathSegment) ? firstPathSegment : "";
    }

    return knownLocales.includes(firstPathSegment) ? firstPathSegment : "";
}

export function getStoredLocale(knownLocales = []) {
    if (typeof window === "undefined") {
        return "";
    }

    const storedLocale = normalizeLocale(
        window.localStorage.getItem(LOCALE_STORAGE_KEY),
    );

    if (!knownLocales.length) {
        return storedLocale;
    }

    return knownLocales.includes(storedLocale) ? storedLocale : "";
}

export function persistLocale(locale) {
    const normalizedLocale = normalizeLocale(locale);

    if (!normalizedLocale || typeof window === "undefined") {
        return;
    }

    window.localStorage.setItem(LOCALE_STORAGE_KEY, normalizedLocale);
}

export function resolvePreferredLocale({
    url = "",
    currentLocale = "",
    siteLanguages = [],
    fallback = "",
}) {
    const knownLocales = getKnownLocales(siteLanguages);
    const localeFromUrl = getLocaleFromUrl(url, knownLocales);

    if (localeFromUrl) {
        return localeFromUrl;
    }

    const normalizedCurrentLocale = normalizeLocale(currentLocale);

    if (normalizedCurrentLocale && !knownLocales.length) {
        return normalizedCurrentLocale;
    }

    if (knownLocales.includes(normalizedCurrentLocale)) {
        return normalizedCurrentLocale;
    }

    const storedLocale = getStoredLocale(knownLocales);

    if (storedLocale) {
        return storedLocale;
    }

    const normalizedFallback = normalizeLocale(fallback);

    if (knownLocales.includes(normalizedFallback)) {
        return normalizedFallback;
    }

    if (normalizedFallback && !knownLocales.length) {
        return normalizedFallback;
    }

    return knownLocales[0] || "";
}

export function buildLocalePath(locale = "", ...segments) {
    const normalizedLocale = normalizeLocale(locale);
    const normalizedSegments = segments
        .filter((segment) => segment !== null && segment !== undefined)
        .map((segment) => `${segment}`.trim())
        .filter(Boolean)
        .map((segment) => segment.replace(/^\/+|\/+$/g, ""))
        .filter(Boolean);

    const prefix = normalizedLocale ? `/${normalizedLocale}` : "";
    const suffix = normalizedSegments.length
        ? `/${normalizedSegments.join("/")}`
        : "";

    return `${prefix}${suffix}` || "/";
}
